/**
 * Axios instance for the public WooCommerce Store API.
 *
 * - baseURL comes from REACT_APP_WC_STORE_URL
 * - Attaches the Cart-Token header on every request (read from sessionStorage)
 * - Captures the Cart-Token header from every response (Store API rotates it
 *   on cart / checkout mutations)
 * - Normalizes errors via parseWooError so callers get a clean Error
 */
import axios from 'axios';
import { getCartToken, setCartToken } from '../cartToken';
import { parseWooError } from '../errors';

const baseURL = process.env.REACT_APP_WC_STORE_URL;

if (!baseURL) {
  // eslint-disable-next-line no-console
  console.warn(
    '[wooClient] REACT_APP_WC_STORE_URL is not set. Store API calls will fail.'
  );
}

export const wooClient = axios.create({
  baseURL,
  timeout: 30_000,
});

wooClient.interceptors.request.use((config) => {
  // Attach Cart-Token if we have one
  const token = getCartToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Cart-Token'] = token;
  }

  // Default JSON content-type for plain object bodies. Don't override if the
  // caller is sending FormData — the browser sets the multipart boundary.
  if (
    config.data &&
    typeof config.data === 'object' &&
    !(config.data instanceof FormData) &&
    !config.headers?.['Content-Type']
  ) {
    config.headers = config.headers || {};
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

wooClient.interceptors.response.use(
  (response) => {
    // Store API rotates the cart token. Always grab the latest from the
    // response header so subsequent requests are authenticated against the
    // same session.
    const cartToken = response.headers?.['cart-token'];
    if (cartToken) {
      setCartToken(cartToken);
    }
    return response;
  },
  (err) => Promise.reject(parseWooError(err))
);

export default wooClient;
